import type { HTMLAttributes, ReactNode } from "react";

import { cx } from "@/lib/utils";

export type PillTone =
  | "neutral"
  | "accent"
  | "ok"
  | "warn"
  | "danger"
  | "custom"
  | "plan";

interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  icon?: ReactNode;
}

const TONE: Record<PillTone, string> = {
  neutral: "bg-surface-2 border-border text-ink-2",
  accent: "bg-accent-bg border-accent-border text-accent-ink",
  ok: "bg-ok-bg border-ok-border text-ok",
  warn: "bg-warn-bg border-warn-border text-warn",
  danger: "bg-danger-bg border-danger-border text-danger",
  custom: "bg-custom-bg border-custom-border text-custom",
  // Inverted on purpose: a filled slab with near-white text, not a
  // saturated colour on a tint. See the ``--plan`` note in globals.css.
  plan: "bg-plan-bg border-plan-border text-plan",
};

export function Pill({
  tone = "neutral",
  icon,
  className,
  children,
  ...rest
}: PillProps): JSX.Element {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium",
        TONE[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </span>
  );
}

